export type ExploreTab = "companies" | "experts" | "corridors" | "expos";

export const CLIENT_EXPLORE_PATH = "/my-discovery";
/** Legacy guest discover base (company/expert profiles still live under `/discover/...`). */
export const GUEST_EXPLORE_PATH = "/discover";

export const GUEST_CORRIDORS_CLUSTERS_PATH = "/corridors-clusters";
export const GUEST_REGIONAL_SECTOR_EXPERTS_PATH = "/regional-sector-experts";
export const GUEST_GLOBAL_EVENTS_EXHIBITIONS_PATH = "/global-events-exhibitions";

export const GUEST_EXPLORE_LIST_PATHS: Record<ExploreTab, string> = {
  companies: "/",
  experts: GUEST_REGIONAL_SECTOR_EXPERTS_PATH,
  corridors: GUEST_CORRIDORS_CLUSTERS_PATH,
  expos: GUEST_GLOBAL_EVENTS_EXHIBITIONS_PATH,
};

const VALID_TABS = new Set<ExploreTab>(["companies", "experts", "corridors", "expos"]);

export function parseExploreTabParam(value: string | null): ExploreTab {
  if (value === "experts" || value === "corridors" || value === "expos") return value;
  return "companies";
}

export function isExploreTab(value: string | null): value is ExploreTab {
  return VALID_TABS.has(parseExploreTabParam(value));
}

/** Logged-in (and partner portal) list URLs still use `?tab=`. */
export function exploreTabHref(basePath: string, tab: ExploreTab): string {
  return tab === "companies" ? basePath : `${basePath}?tab=${tab}`;
}

/** @deprecated Prefer `guestExploreTabHref(tab)` — guest lists no longer use `?tab=`. */
export function guestExploreBasePath(_pathname: string): string {
  return "/";
}

export function guestExploreTabHref(tabOrPathname: ExploreTab | string, tab?: ExploreTab): string {
  // New: guestExploreTabHref("corridors")
  if (tab === undefined && (tabOrPathname === "companies" || tabOrPathname === "experts" || tabOrPathname === "corridors" || tabOrPathname === "expos")) {
    return GUEST_EXPLORE_LIST_PATHS[tabOrPathname];
  }
  // Legacy: guestExploreTabHref(pathname, tab)
  if (tab !== undefined) {
    return GUEST_EXPLORE_LIST_PATHS[tab];
  }
  return GUEST_EXPLORE_LIST_PATHS.companies;
}

export const EXPLORE_EXPERTS_TAB = `${CLIENT_EXPLORE_PATH}?tab=experts`;
export const EXPLORE_CORRIDORS_TAB = `${CLIENT_EXPLORE_PATH}?tab=corridors`;

export const GUEST_EXPLORE_EXPERTS_TAB = GUEST_REGIONAL_SECTOR_EXPERTS_PATH;
export const GUEST_EXPLORE_CORRIDORS_TAB = GUEST_CORRIDORS_CLUSTERS_PATH;

export const EXPLORE_TAB_SUBTITLES: Record<ExploreTab, string> = {
  companies: "Browse verified companies and partnership opportunities.",
  experts: "Verified sector experts who guide cross-border partnerships.",
  corridors: "Cross-border trade corridors led by ecosystem heads.",
  expos: "Upcoming global industry expos and exhibitions.",
};

function tabFromSearch(search: string): ExploreTab {
  const raw = search.startsWith("?") ? search.slice(1) : search;
  return parseExploreTabParam(new URLSearchParams(raw).get("tab"));
}

export function isGuestExploreListPath(pathname: string): boolean {
  return (
    pathname === GUEST_EXPLORE_LIST_PATHS.companies ||
    pathname === GUEST_EXPLORE_LIST_PATHS.experts ||
    pathname === GUEST_EXPLORE_LIST_PATHS.corridors ||
    pathname === GUEST_EXPLORE_LIST_PATHS.expos ||
    pathname === GUEST_EXPLORE_PATH
  );
}

function isClientExploreListPath(pathname: string): boolean {
  return pathname === CLIENT_EXPLORE_PATH;
}

function guestExploreTabFromPath(pathname: string): ExploreTab | null {
  if (pathname === GUEST_EXPLORE_LIST_PATHS.companies || pathname === GUEST_EXPLORE_PATH) {
    return "companies";
  }
  if (pathname === GUEST_EXPLORE_LIST_PATHS.experts) return "experts";
  if (pathname === GUEST_EXPLORE_LIST_PATHS.corridors) return "corridors";
  if (pathname === GUEST_EXPLORE_LIST_PATHS.expos) return "expos";
  return null;
}

/**
 * Resolve which explore tab should appear selected for the current URL.
 * Guest lists use pathname; client lists use `?tab=`; profile pages use pathname
 * so corridor section `?tab=` (profile/company/…) cannot collide with explore state.
 */
export function resolveExploreTab(pathname: string, search = ""): ExploreTab | null {
  const guestListTab = guestExploreTabFromPath(pathname);
  if (guestListTab) return guestListTab;

  if (isClientExploreListPath(pathname)) {
    return tabFromSearch(search);
  }

  // Guest corridor profile (new + legacy)
  if (
    pathname.startsWith(`${GUEST_CORRIDORS_CLUSTERS_PATH}/`) ||
    pathname.startsWith(`${GUEST_EXPLORE_PATH}/corridor/`)
  ) {
    return "corridors";
  }
  // Guest expert profile (new + legacy)
  if (
    /^\/regional-sector-experts\/[^/]+$/.test(pathname) ||
    pathname.startsWith(`${GUEST_EXPLORE_PATH}/expert/`)
  ) {
    return "experts";
  }
  // /discover/:id company profile (single segment after /discover)
  if (/^\/discover\/[^/]+$/.test(pathname)) {
    return "companies";
  }

  // Client expert company anonymous profile
  if (/^\/experts\/company\/[^/]+\/profile$/.test(pathname)) {
    return "experts";
  }

  // Client expert profile: /experts/:industry/:expertUserId
  // Exclude /experts/my/..., /experts/sector/..., /experts/company/...
  {
    const match = pathname.match(/^\/experts\/([^/]+)\/([^/]+)$/);
    if (match) {
      const segment = match[1];
      if (segment !== "my" && segment !== "sector" && segment !== "company") {
        return "experts";
      }
    }
  }

  // Client corridor profile: /ecosystem-heads/:industry/:headUserId
  // Exclude /ecosystem-heads/my/..., shortlist, sector; exclude /announcements suffix
  {
    const match = pathname.match(/^\/ecosystem-heads\/([^/]+)\/([^/]+)$/);
    if (match) {
      const segment = match[1];
      if (segment !== "my" && segment !== "shortlist" && segment !== "sector") {
        return "corridors";
      }
    }
  }

  return null;
}

export function isClientExploreProfileChromePath(pathname: string): boolean {
  if (isClientExploreListPath(pathname) || pathname.startsWith(`${CLIENT_EXPLORE_PATH}/`)) {
    return false;
  }
  const tab = resolveExploreTab(pathname, "");
  return tab === "experts" || tab === "corridors";
}


export function guestExploreListPathFromTabParam(tab: string | null): string {
  return GUEST_EXPLORE_LIST_PATHS[parseExploreTabParam(tab)];
}
